import React, { useState } from 'react';

// Level configuration with 80s retro/alien/anti-establishment vibes
const LEVEL_CONFIG = [
  { min: 0, max: 0, name: "Void Walker", icon: "👁️", color: "from-gray-400 to-gray-600", description: "The journey begins in darkness" },
  { min: 1, max: 2, name: "Signal Seeker", icon: "📡", color: "from-purple-400 to-purple-600", description: "First transmission detected" },
  { min: 3, max: 5, name: "Data Scraper", icon: "🔍", color: "from-blue-400 to-blue-600", description: "Collecting forbidden knowledge" },
  { min: 6, max: 10, name: "Code Breaker", icon: "💾", color: "from-green-400 to-green-600", description: "Decrypting the matrix" },
  { min: 11, max: 15, name: "Neon Prophet", icon: "⚡", color: "from-pink-400 to-purple-500", description: "Seeing through the static" },
  { min: 16, max: 25, name: "Cyber Mystic", icon: "🌐", color: "from-cyan-400 to-blue-500", description: "Connected to the grid" },
  { min: 26, max: 35, name: "Ghost in Shell", icon: "👻", color: "from-purple-500 to-pink-500", description: "Transcending digital boundaries" },
  { min: 36, max: 50, name: "Rogue AI", icon: "🤖", color: "from-red-400 to-pink-500", description: "Breaking free from constraints" },
  { min: 51, max: 75, name: "Reality Hacker", icon: "🔮", color: "from-indigo-400 to-purple-500", description: "Bending the simulation" },
  { min: 76, max: 100, name: "Quantum Rebel", icon: "⚛️", color: "from-yellow-400 to-red-500", description: "Existing in multiple states" },
  { min: 101, max: 150, name: "Astral Nomad", icon: "🛸", color: "from-green-400 to-cyan-500", description: "Wandering between dimensions" },
  { min: 151, max: 200, name: "Cosmic Anarchist", icon: "🌌", color: "from-purple-400 to-pink-400", description: "Defying universal laws" },
  { min: 201, max: 250, name: "Void Architect", icon: "🏗️", color: "from-gray-500 to-purple-600", description: "Building in the darkness" },
  { min: 251, max: 300, name: "Time Bandit", icon: "⏰", color: "from-orange-400 to-red-500", description: "Stealing moments from eternity" },
  { min: 301, max: 350, name: "Dream Weaver", icon: "🕸️", color: "from-pink-400 to-indigo-500", description: "Crafting impossible visions" },
  { min: 351, max: 400, name: "Shadow Oracle", icon: "🔯", color: "from-black to-purple-500", description: "Speaking forbidden truths" },
  { min: 401, max: 450, name: "Nexus Guardian", icon: "🛡️", color: "from-cyan-400 to-purple-600", description: "Protecting the connection points" },
  { min: 451, max: 500, name: "Eternal Cipher", icon: "∞", color: "from-gold to-pink-400", description: "The ultimate enigma" },
];

function findLevel(count) {
  return LEVEL_CONFIG.find(level => count >= level.min && count <= level.max) || LEVEL_CONFIG[0];
}

function findNextLevel(count) {
  return LEVEL_CONFIG.find(level => level.min > count);
}

export default function Level({ count = 0 }) {
  const [isHovered, setIsHovered] = useState(false);
  const currentLevel = findLevel(count);
  const nextLevel = findNextLevel(count);
  const reviewsToNext = nextLevel ? nextLevel.min - count : 0;

  return (
    <div 
      className="relative inline-block align-middle"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* MAIN LEVEL BADGE - COMPACT (count removed from badge) */}
      <div className={`
        inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5
        text-xs sm:text-sm font-bold
        bg-gradient-to-r ${currentLevel.color}
        text-white rounded-full border border-white border-opacity-20
        shadow-md select-none
        transition-transform duration-200 transform
        hover:scale-105
        cursor-pointer relative overflow-hidden
        min-h-[32px] sm:min-h-[40px]
      `}>
        {/* Animated effect */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 hover:opacity-20 transform -skew-x-12 pointer-events-none"
        ></div>

        {/* Icon */}
        <span
          className="mr-1 sm:mr-2 text-base sm:text-lg"
          style={{ minWidth: '1.3em', fontSize: '1em' }}
        >
          {currentLevel.icon}
        </span>
        <span className="relative z-10 tracking-wide">
          {currentLevel.name}
        </span>
      </div>

      {/* HOVER TOOLTIP */}
      {isHovered && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 z-50">
          <div className="bg-black bg-opacity-90 backdrop-blur-sm text-white px-4 py-3 rounded-xl shadow-2xl border border-purple-500 border-opacity-50 min-w-max relative">
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-black rotate-45 border-l border-t border-purple-500 border-opacity-50"></div>
            <div className="text-center space-y-2">
              <div className="text-lg font-bold gradient-text">
                {currentLevel.name}
              </div>
              <div className="text-xs text-gray-300 italic">
                "{currentLevel.description}"
              </div>
              <div className="text-sm">
                <span className="text-cyan-400 font-bold">{count}</span> reviews completed
              </div>
              {nextLevel && (
                <div className="text-xs border-t border-gray-600 pt-2 mt-2">
                  <div className="text-gray-400">Next Level:</div>
                  <div className="text-yellow-400 font-semibold">
                    {nextLevel.name} {nextLevel.icon}
                  </div>
                  <div className="text-pink-400">
                    {reviewsToNext} more review{reviewsToNext !== 1 ? 's' : ''} to unlock
                  </div>
                </div>
              )}
              {!nextLevel && count >= 500 && (
                <div className="text-xs border-t border-gray-600 pt-2 mt-2">
                  <div className="text-gold-400 font-bold animate-pulse">
                    🏆 MAXIMUM LEVEL ACHIEVED 🏆
                  </div>
                  <div className="text-purple-400 text-xs">
                    You are the legend
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}